import classNames from 'classnames'
import { type FC, type ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { GroupCheckbox } from '../GroupCheckbox/GroupCheckbox'

export type CheckboxOption = { id: string; label: string }

interface ExpandableCheckboxCardProps {
  title: string
  subtitle?: ReactNode
  options: CheckboxOption[]
  selectedIds: string[]
  isExpanded: boolean
  disabled?: boolean
  hideSelectionControls?: boolean
  headerTrailing?: ReactNode
  listHeader?: ReactNode
  renderRowTrailing?: (option: CheckboxOption) => ReactNode
  idPrefix: string
  onToggleExpand: () => void
  onToggleOption: (optionId: string) => void
  onToggleAll: (allCurrentlySelected: boolean) => void
}

export const ExpandableCheckboxCard: FC<ExpandableCheckboxCardProps> = ({
  title,
  subtitle,
  options,
  selectedIds,
  isExpanded,
  disabled,
  hideSelectionControls,
  headerTrailing,
  listHeader,
  renderRowTrailing,
  idPrefix,
  onToggleExpand,
  onToggleOption,
  onToggleAll,
}) => {
  const optionIds = options.map((option) => option.id)
  const selected = new Set(selectedIds)

  return (
    <div
      className={classNames('rounded-2 border zocks-border-color-gray-300 zocks-bg-color-white', {
        'zocks-py-1.25': subtitle != null,
        'zocks-py-2.0': subtitle == null,
      })}
    >
      <div className='d-flex align-items-center justify-content-between zocks-gap-2 px-3'>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          {hideSelectionControls ? null : (
            <GroupCheckbox options={optionIds} selectedOptions={selectedIds} onToggle={(_options, allEnabled) => onToggleAll(allEnabled)} disabled={disabled} />
          )}
          <Button className='d-flex flex-column align-items-start text-start p-0 zocks-gap-0.25' onClick={onToggleExpand} aria-expanded={isExpanded}>
            <span className='paragraph-medium fw-bold zocks-text-gray-900'>{title}</span>
            {subtitle != null ? <span className='label-medium'>{subtitle}</span> : null}
          </Button>
        </div>
        <div className='d-flex align-items-center zocks-gap-2'>
          {headerTrailing}
          <Button className='p-0' onClick={onToggleExpand} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}>
            <i className={classNames('bi zocks-text-gray-600', isExpanded ? 'bi-chevron-contract' : 'bi-chevron-expand')} />
          </Button>
        </div>
      </div>

      {isExpanded ? (
        <>
          {listHeader != null ? <div className='mx-4 ps-4 zocks-pt-2.5 zocks-pb-0.5'>{listHeader}</div> : null}
          {options.map((option, index) => (
            <div
              key={option.id}
              className={classNames('d-flex align-items-center zocks-gap-0.5 zocks-pt-0.75 mx-4 ps-4', {
                'border-top zocks-border-color-gray-300': index !== 0,
                'zocks-pb-0.75': index !== options.length - 1,
              })}
            >
              {hideSelectionControls ? null : (
                <Form.Check
                  type='checkbox'
                  id={`${idPrefix}-${option.id}`}
                  checked={selected.has(option.id)}
                  onChange={() => onToggleOption(option.id)}
                  aria-label={option.label}
                  disabled={disabled}
                  className='d-flex justify-content-center align-items-center'
                />
              )}
              <span className={classNames('flex-grow-1 paragraph-medium', selected.has(option.id) ? 'zocks-text-gray-900' : 'zocks-text-gray-600')}>
                {option.label}
              </span>
              {renderRowTrailing?.(option)}
            </div>
          ))}
        </>
      ) : null}
    </div>
  )
}
